import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FilterMatchMode } from 'primereact/api';
import axios from 'axios';
import { selectModuleMenuData, selectLoading, selectSelectedModuleGroup, fetchModuleMenu } from '../store/slices/modulemenuSlice';
import ModuleMenuDialog from './ModuleMenuDialog';
import { getSalesReactBaseUrl } from '../utilities/api';
import './CategoryMaster.css';

const API_URL = `${getSalesReactBaseUrl()}/api/server_masters`;

const ModuleMenuContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);

    const moduleMenuData = useSelector(selectModuleMenuData);
    const loading = useSelector(selectLoading);
    const selectedModuleGroup = useSelector(selectSelectedModuleGroup);

    const [statusFilter, setStatusFilter] = useState('Active');
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [displayDialog, setDisplayDialog] = useState(false);
    const [editMenu, setEditMenu] = useState(null);
    const [groupOptions, setGroupOptions] = useState([]);

    useEffect(() => {
        axios
            .get(`${API_URL}/getModuleGroups`)
            .then((res) => {
                if (Array.isArray(res.data)) {
                    setGroupOptions(res.data.map((g) => ({ label: g.module_group_name, value: g.module_group_id })));
                }
            })
            .catch(() => {});
    }, []);

    const groupFilteredData = useMemo(() => {
        if (!selectedModuleGroup) return moduleMenuData;
        return moduleMenuData.filter((item) => String(item.module_group_id) === String(selectedModuleGroup));
    }, [moduleMenuData, selectedModuleGroup]);

    const activeCount = useMemo(() => groupFilteredData.filter((item) => item.status === 'Active').length, [groupFilteredData]);
    const inactiveCount = useMemo(() => groupFilteredData.filter((item) => item.status === 'Inactive' || item.status === 'InActive').length, [groupFilteredData]);

    const filteredData = useMemo(() => groupFilteredData.filter((item) => item.status === statusFilter), [groupFilteredData, statusFilter]);

    const reload = () => dispatch(fetchModuleMenu());

    const handleSaveRequest = (menuItem) => {
        const isEdit = !!menuItem.module_menu_id;
        confirmDialog({
            message: isEdit ? 'Are you sure you want to update this module menu?' : 'Are you sure you want to add this new module menu?',
            header: isEdit ? 'Confirm Update' : 'Confirm Add',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Save',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text',
            accept: () => handleConfirmedSave(menuItem)
        });
    };

    const handleConfirmedSave = async (menuItem) => {
        const isEdit = !!menuItem.module_menu_id;
        try {
            const endpoint = isEdit ? `${API_URL}/updateModuleMenu/${menuItem.module_menu_id}` : `${API_URL}/addModuleMenu`;
            const res = await axios.post(endpoint, menuItem, {
                headers: { 'Content-Type': 'application/json' }
            });
            const msg = res.data?.message || (isEdit ? 'Module menu updated successfully' : 'Module menu added successfully');
            toast.current.show({ severity: 'success', summary: 'Success', detail: msg, life: 3000 });
            setDisplayDialog(false);
            setEditMenu(null);
            reload();
        } catch (err) {
            const detail = err?.response?.data?.message || err?.message || 'Save failed. Please try again.';
            toast.current.show({ severity: 'error', summary: 'Error', detail, life: 4000 });
        }
    };

    const statusBodyTemplate = (rowData) => <Tag value={rowData.status} severity={rowData.status === 'Active' ? 'success' : 'danger'} rounded style={{ minWidth: '70px', textAlign: 'center' }} />;

    const actionBodyTemplate = (rowData) => (
        <Button
            icon="pi pi-pencil"
            rounded
            text
            className="p-button-warning"
            onClick={() => {
                setEditMenu(rowData);
                setDisplayDialog(true);
            }}
            tooltip="Edit"
            tooltipOptions={{ position: 'top' }}
        />
    );

    return (
        <Card className="bill-reports-container">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-semibold m-0">Module Menu List</h3>
                    <div className="category-status-tabs">
                        <button className={`status-tab-btn active-tab${statusFilter === 'Active' ? ' selected' : ''}`} onClick={() => setStatusFilter('Active')}>
                            <i className="pi pi-check-circle" />
                            Active <span className="tab-count">{activeCount}</span>
                        </button>
                        <button className={`status-tab-btn inactive-tab${statusFilter === 'Inactive' ? ' selected' : ''}`} onClick={() => setStatusFilter('Inactive')}>
                            <i className="pi pi-times-circle" />
                            Inactive <span className="tab-count">{inactiveCount}</span>
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 align-items-center">
                    <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={(e) => {
                                setGlobalFilterValue(e.target.value);
                                setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                            }}
                            placeholder="Search module menus..."
                            className="w-full"
                        />
                    </span>
                    <Button
                        label="Add Module Menu"
                        icon="pi pi-plus"
                        className="p-button-primary"
                        onClick={() => {
                            setEditMenu(null);
                            setDisplayDialog(true);
                        }}
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="sub-masters-table-wrap">
                    <DataTable
                        value={filteredData}
                        filters={filters}
                        globalFilterFields={['module_name', 'module_group_name', 'link', 'icon']}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                        showGridlines
                        tableStyle={{ minWidth: '40rem' }}
                        className="p-datatable-sm sub-masters-table"
                        emptyMessage={`No ${statusFilter.toLowerCase()} module menus found`}
                    >
                        <Column field="module_menu_id" header="Menu ID" sortable style={{ width: '100px' }} />
                        <Column field="module_name" header="Module Name" sortable />
                        <Column field="module_group_name" header="Module Group" sortable />
                        <Column field="link" header="Link" sortable />
                        <Column field="icon" header="Icon" sortable style={{ width: '160px' }} />
                        <Column field="status" header="Status" body={statusBodyTemplate} sortable style={{ width: '120px', textAlign: 'center' }} />
                        <Column header="Actions" body={actionBodyTemplate} style={{ width: '90px', textAlign: 'center' }} />
                    </DataTable>
                </div>
            )}

            <ModuleMenuDialog
                visible={displayDialog}
                menuData={editMenu}
                groupOptions={groupOptions}
                onHide={() => {
                    setDisplayDialog(false);
                    setEditMenu(null);
                }}
                onSave={handleSaveRequest}
            />
        </Card>
    );
};

export default ModuleMenuContent;
